import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { getAccessStatus, redeemInviteCode } from "@/lib/access.functions";
import { PlanetMark } from "@/components/planet-mark";
import { Ticket, LogOut } from "lucide-react";

export const Route = createFileRoute("/welcome")({
  component: Welcome,
  head: () => ({
    meta: [
      { title: "You're on the list · Pyng" },
      { name: "description", content: "Pyng is invite-only. Check your place in line or enter your invite code." },
      { property: "og:title", content: "You're on the list · Pyng" },
      { property: "og:description", content: "Pyng is invite-only. Check your place in line or enter your invite code." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function Welcome() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const status = useServerFn(getAccessStatus);
  const redeem = useServerFn(redeemInviteCode);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login", replace: true });
  }, [user, loading, navigate]);

  // A code can arrive in the invite link: /welcome?code=PYNG-XXXX
  useEffect(() => {
    const c =
      new URLSearchParams(window.location.search).get("code") ??
      sessionStorage.getItem("pyng:invite-code");
    if (c) setCode(c.toUpperCase());
  }, []);

  const { data } = useQuery({
    queryKey: ["access-status", user?.id],
    queryFn: () => status(),
    enabled: !!user,
  });

  useEffect(() => {
    if (data?.approved) navigate({ to: "/today", replace: true });
  }, [data, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const res: any = await redeem({ data: { code: code.trim() } });
      if (!res?.ok) {
        toast.error(res?.reason ?? "That code isn't valid");
        return;
      }
      toast.success(res.plan && res.plan !== "free" ? `You're in — ${String(res.plan).toUpperCase()} unlocked.` : "You're in.");
      sessionStorage.removeItem("pyng:invite-code");
      await qc.invalidateQueries();
      navigate({ to: "/today", replace: true });
    } catch (err: any) {
      toast.error(err?.message ?? "Could not redeem that code");
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    navigate({ to: "/login", replace: true });
  };

  return (
    <main className="relative mx-auto flex min-h-[100svh] max-w-md items-center justify-center px-6">
      <Link to="/" className="absolute left-4 top-4 flex items-center gap-2 text-foreground/90 hover:text-foreground sm:left-6 sm:top-6" aria-label="Pyng home">
        <PlanetMark size={28} />
        <span className="text-sm font-medium tracking-tight">Pyng</span>
      </Link>

      <div className="glass w-full rounded-3xl p-8 animate-in-fade">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          <span className="neon-pulse-dot" aria-hidden />
          Invite only
        </div>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">You're on the list</h1>
        {data && !data.approved && data.waitlistPosition ? (
          <p className="mt-2 text-sm text-muted-foreground">
            You're <span className="font-semibold text-foreground">#{data.waitlistPosition}</span> in line. We're opening Pyng to a
            small group at a time so every account gets a real signal engine behind it.
          </p>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            We're opening Pyng to a small group at a time. You'll get an invite code by email when a seat opens.
          </p>
        )}

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="relative">
            <Ticket className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Invite code"
              autoComplete="off"
              className="h-11 w-full rounded-xl border border-border bg-card px-4 pl-10 text-sm uppercase tracking-wider outline-none transition-colors focus:border-[var(--accent-neon)] focus:ring-2 focus:ring-[color-mix(in_oklab,var(--accent-neon)_28%,transparent)] placeholder:text-muted-foreground placeholder:normal-case placeholder:tracking-normal"
            />
          </div>
          <button
            disabled={busy || !code.trim()}
            className="h-11 w-full rounded-xl bg-primary text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-px disabled:opacity-60"
          >
            {busy ? "Checking…" : "Unlock Pyng"}
          </button>
        </form>

        <button onClick={signOut} className="mt-5 inline-flex w-full items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </main>
  );
}
